import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import toastConfig from '../app/toast-config';
import { addNewSensorData, updateLiveReadings } from '../features/sensors/sensorSlice';
import { updateRules } from '../features/rules/ruleSlice';

const Websockets = forwardRef(function Websockets({
    setOutputStates,
    setWsConnected,
    setIsManualControlEnabled,
    setWebsocket,
    setIsClientSet,
    setControlMode,
    setWaterLevelLow,
    setClientId
}, ref) {
    const socketRef = useRef(null);
    const reconnectTimerRef = useRef(null);
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const clientId = localStorage.getItem('clientId');
    const { currentDevice } = useSelector((state) => state.deviceData);
	const isConnectingRef = useRef(false);
	
	const cleanupSocket = () => {
	    if (socketRef.current) {
	      socketRef.current.onclose = null;
	      socketRef.current.onerror = null;
	      socketRef.current.onmessage = null;
	      socketRef.current.onopen = null;
	      socketRef.current.close();
	      socketRef.current = null;
	    }
	};

    const attemptReconnect = () => {
        if (reconnectTimerRef.current) return; // Prevent multiple timers
		console.log("Attempting WebSocket reconnect in 5 seconds...");
        reconnectTimerRef.current = setTimeout(() => {
          reconnectTimerRef.current = null;
          connectWebSocket();
        }, 5000); // Try every 5 seconds
    };

    const connectWebSocket = () => {
        if (!user || !currentDevice || isConnectingRef.current) return;
        
        isConnectingRef.current = true;
        
        console.log(`Client id from storage: ${clientId}`);
        const baseURL = process.env.REACT_APP_SERVER_URL;
        const wsProtocol = baseURL.startsWith('https') ? 'wss' : 'ws';
        const host = baseURL.replace(/^https?:\/\//, '');
        let params = `isReactClient=true&user=${user._id}`;
        if (clientId) params += `&reactClientId=${clientId}`;
        params += `&currentDevice=${currentDevice.id}`;
        const wsURL = `${wsProtocol}://${host}/ws?${params}`;
        
        const websocket = new WebSocket(wsURL);

        websocket.onopen = () => {
            console.log('WebSocket Client connected.');
            if (!socketRef.current || socketRef.current !== websocket) {
                setWebsocket(websocket);         // Only if not already set
                setIsClientSet(true);            // Only trigger if not already true
            }
        
            setWsConnected(true);
            toast.success('WebSocket Client connected.', toastConfig);
        
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
            isConnectingRef.current = false;
        
            const json = {
                action: "get_output_states",
                user: user._id,
                deviceId: currentDevice.id
            };
        
            websocket.send(JSON.stringify(json));
        };

        websocket.onmessage = (message) => {
            const data = JSON.parse(message.data);
            switch (data.action) {
                case "update_id":
                    console.log(`Adding client id to the local storage: ${data.id}`);
                    localStorage.setItem('clientId', data.id);
                    setClientId(data.id);
                    const json = {
                        action: "update_current_device",
                        id: data.id,
                        currentDevice: currentDevice.id
                    };
                    if (websocket?.readyState === WebSocket.OPEN) {
                        console.log("Sending current device data to the server.");
                        websocket.send(JSON.stringify(json));
                    }
                    break;
                case "output_states":
                    console.log("Received output states:");
                    console.log(JSON.stringify(data.output_states));
                    setOutputStates({ ...data.output_states });
                    // setIsManualControlEnabled(true);
                    break;
                case "new_sensor_data":
                    console.log("Received sensor data:");
                    console.log(JSON.stringify(data.sensor_data));
                    if (data.scheduled) {
                        console.log("Scheduled data received, pushing to chart data");
                        dispatch(addNewSensorData(data.sensor_data));
                    }
                    else {
                        dispatch(updateLiveReadings(data.sensor_data));
                    }
                    // setIsManualControlEnabled(true);
                    break;
                case "get_rules_response":
                    console.log("Received rules.");
                    // console.log(JSON.stringify(data.rules));
                    dispatch(updateRules(data.rules));
                    break;
                case "set_control_mode_react_client":
                    console.log(`Received control mode change to ${data.mode}`);
                    setControlMode(data.mode);
                    // setIsManualControlEnabled(true);
                    break;
                case "set_control_mode_response":
                    setControlMode(data.mode);
                    break;
                case "error":
                    console.log(`Received error from server: ${data.message}`);
                    if (data.message.includes("Water level low")) setWaterLevelLow(true)
                        else toast.error(`Error: ${data.message}`, toastConfig);
                    break;
                default:
                    console.warn("Unhandled WS message:", data);
            }
        };

        websocket.onclose = () => {
            console.log("WebSocket Client disconnected.");
            toast.info("WebSocket Client disconnected.", toastConfig);
            setWsConnected(false);
            isConnectingRef.current = false;
            attemptReconnect();
        };

        websocket.onerror = (err) => {
            console.error('WebSocket error:', err);
            isConnectingRef.current = false;
            websocket.close(); // Close to trigger reconnect via onclose
        };

        cleanupSocket();
        socketRef.current = websocket;
    };

    useEffect(() => {
        if (!user || !currentDevice?.id) return;
    
        const shouldConnect =
            !socketRef.current ||
            socketRef.current.readyState === WebSocket.CLOSED ||
            socketRef.current.readyState === WebSocket.CLOSING;
    
        if (shouldConnect) {
            connectWebSocket();
        }
        else {
            console.log("WebSocket already connected or connecting. Skipping reconnect.");
            toast.info("WebSocket already connected or connecting. Skipping reconnect.", toastConfig);
        }
    
        return () => {
            cleanupSocket();
            clearTimeout(reconnectTimerRef.current);
        };
    }, [user?._id, currentDevice?.id]);

    useImperativeHandle(ref, () => socketRef.current, []);
    
    return null;
});

export default Websockets;
